/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { useQuery, useQueryClient, useMutation } from 'react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';

import Navigation from '../../components/Navigation';
import Comments from '../../components/Meeting/Comments';
import SignIn from '../../components/Meeting/SignIn';
import Breadcrumbs from '../../components/Breadcrumbs';
import Layout from '../../containers/Layout';
import ConfirmReviewModal from '../../modals/ConfirmReviewModal';
import MultiButtonDialogModal from '../../modals/MultiButtonDialogModal';
import VideoApproval from '../../components/Meeting/VideoApproval';
import FilledButton from '../../components/Buttons/FilledButton';

import { hasAuthData } from '../../helpers/authStorage';
import MeetingService from '../../services/MeetingService';
import UserService from '../../services/UserService';
import CommentService from '../../services/CommentService';
import TrainingPathService from '../../services/TrainingPathService';
import { UiContext } from '../../context/UiContext';
import classes from '../MeetingPage/styles.module.scss';
import newClasses from './styles.module.scss';
import { useUiStore } from '../../store';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import { isTrainingPathsEnabledForOrg } from '../../constants/main';

// New player infrastructure
import { CreateVideoNewPageProvider } from '../CreateVideoNewPage/store/Provider';
import { VideoRenderer, ImageRenderer, DocumentRenderer, QuestionRenderer, TextRenderer, AudioRenderer, AssignedSectionRenderer } from '../CreateVideoNewPage/components/PreviewRenderers';
import SeekingBar from '../CreateVideoNewPage/components/SeekingBar';
import FooterTimeline from '../CreateVideoNewPage/components/FooterTimeline';
import { selectAllAssets, addAssets, clearAssets, updateAsset } from '../CreateVideoNewPage/store/slices/assetsSlice';
import { selectTotalDuration, reorderItems, clearTimeline, selectItem } from '../CreateVideoNewPage/store/slices/timelineSlice';
import { play, pause, seek, reset as resetPlayback, togglePlayPause } from '../CreateVideoNewPage/store/slices/playbackSlice';
import {
  setPreviewMode,
  setQuestionModalVisible,
  setViewImageSectionModalVisible,
  setViewUploadDocumentsModalVisible,
  markSectionAsHandled,
  clearHandledSection,
  clearAllHandledSections,
  setIsFullscreen,
} from '../CreateVideoNewPage/store/slices/editorUISlice';
import { useSectionsPagination } from '../CreateVideoNewPage/hooks/useSectionsPagination';
import { loadPublishedMeeting } from './lib/loadPublishedMeeting';
import { convertSectionsToTimeline } from '../CreateVideoNewPage/lib/convertSections';
import ViewerModalsContainer from './components/ViewerModalsContainer';
import snapsopLogo from '../../assets/images/snapsop-transparent.svg';
import backIcon from '../../assets/images/video-player/back.svg';
import headerStyles from '../../components/VideoPlayerOverlay/Header/styles.module.scss';
import PreviewContainer from '../CreateVideoNewPage/components/PreviewContainer';
import useAuthorization from '../../hooks/useAuthorization';

function MeetingPageNewContent({
  meeting,
  accessPassword,
  getSharedMeeting,
  preview,
  email,
}) {
  const dispatch = useDispatch();
  const [questions, setQuestions] = useState([]);
  const [isSignUpPopupVisible, setIsSignUpPopupVisible] = useState(false);
  const [isConfirmReviewModalVisible, setIsConfirmReviewModalVisible] =
    useState(false);
  const [reviewDecision, setReviewDecision] = useState('');
  const [trainingPathCompleteModalData, setTrainingPathCompleteModalData] =
    useState(null);
  const [
    trainingPathCompletionDismissed,
    setTrainingPathCompletionDismissed,
  ] = useState(false);

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const meetingVersion = queryParams.get('version');
  const reviewId = queryParams.get('reviewId');
  const resumeSectionId = queryParams.get('resumeSection');

  const videoRef = useRef(null);
  const miniVideoRef = useRef(null);
  const footerTimelineRef = useRef();
  const fullscreenContainerRef = useRef();
  const shouldReturnToFullscreenRef = useRef(false);
  const isViewConfirmedRef = useRef(false);
  const completedSectionsRef = useRef(new Set());
  const lastTimelineItemRef = useRef(null);

  const breadcrumbsOptions = useUiStore((state) => state.breadcrumbsOptions);
  const setBreadcrumbsOptions = useUiStore((state) => state.setBreadcrumbsOptions);

  const isMobile = useCheckMobileScreen();
  const previewContainerStyle = isMobile
    ? {
        height: '70vh',
        minHeight: '70vh',
        borderRadius: 0,
      }
    : null;

  const isAuthenticated = hasAuthData();
  const { setRedirectAfterLogin, setIsSignInHintVisible, notifyError } =
    useContext(UiContext);

  const queryClient = useQueryClient();

  // Fetch user only if authenticated - needed for badges and other features
  // Don't fetch for anonymous users (shared meeting flow) to avoid unnecessary errors
  const { data: user } = useQuery('me', UserService.getMe, {
    enabled: isAuthenticated,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Compute training path config early so queries below see the correct values
  const trainingPathConfig = useMemo(() => {
    const enabled = isTrainingPathsEnabledForOrg(user);
    const assignmentId = enabled ? queryParams.get('trainingPathAssignmentId') : null;
    const hasAssignment = enabled && Boolean(assignmentId);
    return {
      trainingPathAssignmentId: assignmentId,
      hasTrainingPathAssignment: hasAssignment,
    };
  }, [user, queryParams]);

  const { trainingPathAssignmentId, hasTrainingPathAssignment } = trainingPathConfig;


  // Redux state from new player infrastructure
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const timelineItems = useSelector((state) => state.timeline.items);
  const allAssets = useSelector(selectAllAssets);
  const totalDuration = useSelector(selectTotalDuration);

  // Modal visibility from Redux
  const isQuestionModalVisible = useSelector((state) => state.editorUI.isQuestionModalVisible);
  const isViewImageSectionModalVisible = useSelector((state) => state.editorUI.isViewImageSectionModalVisible);
  const isViewUploadDocumentsModalVisible = useSelector((state) => state.editorUI.isViewUploadDocumentsModalVisible);
  const handledSectionIds = useSelector((state) => state.editorUI.handledSectionIds);

  // Check if any modal is visible
  const isAnyModalVisible = isQuestionModalVisible || isViewImageSectionModalVisible || isViewUploadDocumentsModalVisible;

  // Find current item based on currentTime
  const effectiveTime = isPlaying
    ? currentTime
    : Math.max(0, currentTime - 0.001);

  const getCurrentItemFromTime = () => {
    const exactMatch = timelineItems.find(
      (item) => Math.abs(currentTime - item.start_time) < 0.01
    );
    if (exactMatch) return exactMatch;

    if (!isPlaying) {
      const pausedAtEnd = timelineItems.find((item) => {
        const itemEnd = item.start_time + item.duration;
        return Math.abs(currentTime - itemEnd) < 0.01;
      });
      if (pausedAtEnd) return pausedAtEnd;
    }

    return (
      timelineItems.find((item) => {
        const itemEnd = item.start_time + item.duration;
        return effectiveTime >= item.start_time && effectiveTime < itemEnd;
      }) ||
      (timelineItems.length > 0 ? timelineItems[timelineItems.length - 1] : null)
    );
  };

  let currentItem;
  if (isPlaying || currentTime > 0) {
    currentItem = getCurrentItemFromTime();
  } else {
    currentItem = timelineItems.length > 0 ? timelineItems[0] : null;
  }

  const currentAsset = currentItem
    ? allAssets.find((a) => a.id === currentItem.asset_id)
    : null;

  // Timeline pagination (disable auto-advance in viewer mode)
  const pagination = useSectionsPagination({
    timelineItems,
    disableAutoAdvance: true,
    currentItemId: currentItem?.id || null,
  });
  const {
    sectionsListContainerRef,
    sectionsListRef,
    sectionsCount,
    currentSectionsPage,
    setCurrentSectionsPage,
    sectionsPageCount,
    scrollSections,
  } = pagination;

  // Get sectionsListTranslateX from UI store
  const sectionsListTranslateX = useUiStore((state) => state.sectionsListTranslateX);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      dispatch(setIsFullscreen(isFullscreen));
      
      // If user manually exits fullscreen (not through modal), reset the return flag
      if (!isFullscreen && !isAnyModalVisible) {
        shouldReturnToFullscreenRef.current = false;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [dispatch, isAnyModalVisible]);

  // Exit fullscreen when modals open (modals are rendered outside fullscreen container)
  // Track that we should return to fullscreen after modal closes
  useEffect(() => {
    if (isAnyModalVisible && document.fullscreenElement) {
      shouldReturnToFullscreenRef.current = true;
      document.exitFullscreen().then(() => {
        dispatch(setIsFullscreen(false));
      }).catch((fullscreenError) => {
        console.error('Error exiting fullscreen for modal:', fullscreenError);
      });
    }
  }, [isAnyModalVisible, dispatch]);

  // Return to fullscreen after modal closes and we continue to next section
  const returnToFullscreen = useCallback(() => {
    if (shouldReturnToFullscreenRef.current && fullscreenContainerRef.current?.requestFullscreen) {
      // Small delay to ensure modal is fully closed before returning to fullscreen
      setTimeout(() => {
        if (shouldReturnToFullscreenRef.current && fullscreenContainerRef.current?.requestFullscreen) {
          fullscreenContainerRef.current.requestFullscreen().then(() => {
            dispatch(setIsFullscreen(true));
            shouldReturnToFullscreenRef.current = false;
          }).catch((fullscreenReturnError) => {
            console.error('Error returning to fullscreen:', fullscreenReturnError);
            shouldReturnToFullscreenRef.current = false;
          });
        }
      }, 100);
    }
  }, [dispatch]);

  // Find current item based on currentTime
  // Fetch meeting data
  const { data, refetch, isError } = useQuery(
    ['viewMeeting', id, meetingVersion],
    () => MeetingService.getMeeting(id, meetingVersion),
    {
      enabled: !meeting,
      retry: false,
    }
  );

  const { data: comments, refetch: refetchComments } = useQuery(
    ['comments', id],
    () => CommentService.getMeetingComments(id),
    {
      enabled: !meeting,
      keepPreviousData: true,
      retry: false,
    }
  );

  const { mutateAsync: updateMeeting } = useMutation(
    MeetingService.updateMeeting,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['viewMeeting', id, meetingVersion]);
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update video';
        notifyError(errorMessage);
      },
    }
  );

  // Fetch training path assignment and content for playlist sidebar
  const { data: assignmentData, isLoading: isLoadingAssignment } = useQuery(
    ['training-path-assignment', trainingPathAssignmentId],
    () => TrainingPathService.getAssignment(trainingPathAssignmentId),
    {
      enabled: hasTrainingPathAssignment && Boolean(trainingPathAssignmentId),
      retry: false,
    }
  );

  // Optimistic update for training path progress
  const updateTrainingPathProgress = useCallback((meetingId) => {
    if (!hasTrainingPathAssignment || !assignmentData?.TrainingPath?.id) return;

    const queryKey = ['training-path', assignmentData.TrainingPath.id, 'trainee', meetingId];

    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData?.content) return oldData;

      return {
        ...oldData,
        content: oldData.content.map((video) => {
          if (video.id === meetingId) {
            // Simple progress calculation: 100% / total sections in video
            const totalSections = timelineItems.length;
            const progressIncrement = totalSections > 0 ? (100 / totalSections) : 20;

            const currentProgress = video.progressPercent || 0;
            const newProgressPercent = Math.min(100, currentProgress + progressIncrement);

            // If progress exceeds 95%, consider it complete (handles edge cases)
            const finalProgress = newProgressPercent > 95 ? 100 : Math.round(newProgressPercent);

            return { ...video, progressPercent: finalProgress };
          }
          return video;
        })
      };
    });
  }, [queryClient, hasTrainingPathAssignment, assignmentData, timelineItems]);

  const { data: trainingPathData, isLoading: isLoadingTrainingPath } = useQuery(
    ['training-path', assignmentData?.TrainingPath?.id, 'trainee', id],
    () => TrainingPathService.getTrainingPath(assignmentData?.TrainingPath?.id, 'trainee'),
    {
      enabled:
        hasTrainingPathAssignment &&
        Boolean(assignmentData?.TrainingPath?.id),
      retry: false,
    }
  );

  // Fetch section progress for completed sections check marks
  const { data: sectionProgressData } = useQuery(
    ['section-progress', trainingPathAssignmentId, id],
    () => TrainingPathService.getSectionProgress(trainingPathAssignmentId, id),
    {
      enabled: hasTrainingPathAssignment && Boolean(trainingPathAssignmentId) && Boolean(id),
      retry: false,
    }
  );

  // Create Set of completed section IDs for O(1) lookup
  const completedSectionIds = useMemo(() => {
    if (!sectionProgressData || !Array.isArray(sectionProgressData)) {
      return new Set();
    }
    return new Set(
      sectionProgressData
        .filter((progress) => progress.progress >= 1)
        .map((progress) => progress.sectionId)
    );
  }, [sectionProgressData]);

  const meetingData = data || meeting;
  const resource = useMemo(() => {
    if (!meetingData?.userId) return null;
    return { userId: meetingData.userId };
  }, [meetingData?.userId]);
  const resourceUser = useMemo(() => {
    const role =
      meetingData?.userRole || meetingData?.MeetingUsers?.[0]?.userRole;
    return role ? { role } : null;
  }, [meetingData?.userRole, meetingData?.MeetingUsers]);
  const { canEditMeeting } = useAuthorization(
    user,
    resource,
    resourceUser,
    'meeting',
    {
      folderId: meetingData?.folderId,
      folderUserRole: meetingData?.folderUserRole || null,
    }
  );
  const rawUserRole =
    meetingData?.userRole ||
    meetingData?.MeetingUsers?.[0]?.userRole ||
    meeting?.MeetingUsers?.[0]?.userRole ||
    '';
  const pendingReview =
    Array.isArray(meetingData?.MeetingReviews) &&
    meetingData.MeetingReviews.length > 0;
  const normalizedRawRole =
    rawUserRole?.toLowerCase?.() ||
    (typeof rawUserRole === 'string' ? rawUserRole.toLowerCase() : '');
  const normalizedUserRole =
    reviewId && pendingReview ? 'reviewer' : normalizedRawRole;
  const sections = meetingData?.Sections || [];
  const meetingTitle = meetingData?.title || 'Untitled Video';

  // Keep breadcrumbs in sync with meeting title and folder hierarchy
  // Preserve existing breadcrumbs if user navigated from another page (like legacy viewer)
  useEffect(() => {
    // Wait for meeting data to be loaded before building breadcrumbs
    if (!meetingData || !meetingData.id) {
      return;
    }

    // Check if breadcrumbs already exist (user navigated from My Videos or My Library)
    const existingBreadcrumbs = breadcrumbsOptions;
    
    if (existingBreadcrumbs && existingBreadcrumbs.length > 0) {
      // User navigated from a page - preserve their breadcrumbs and append meeting title
      const lastBreadcrumb = existingBreadcrumbs[existingBreadcrumbs.length - 1];
      
      // Only update if meeting title changed or if last breadcrumb is not the meeting title
      if (lastBreadcrumb.label !== meetingTitle) {
        // Remove the last breadcrumb if it's a placeholder or different meeting
        const breadcrumbsToKeep = lastBreadcrumb.to === '#' 
          ? existingBreadcrumbs.slice(0, -1) 
          : existingBreadcrumbs;
        
    setBreadcrumbsOptions([
          ...breadcrumbsToKeep,
      { label: meetingTitle, to: '#' },
    ]);
      }
    } else {
      // No existing breadcrumbs (direct link, refresh, etc.) - build from folder data
      const breadcrumbs = [];
      
      // If meeting is in a folder, show folder breadcrumbs
      if (meetingData?.parentFolders && meetingData.parentFolders.length > 0) {
        breadcrumbs.push({ label: 'My Library', to: '/my-library' });
        // Add parent folders (includes all parents and the meeting's folder)
        meetingData.parentFolders.forEach((folder) => {
          breadcrumbs.push({
            label: folder.name,
            to: `/my-library/${folder.id}`,
          });
        });
        // Add meeting title as final breadcrumb
        breadcrumbs.push({ label: meetingTitle, to: '#' });
      } else {
        // No folder - use simple breadcrumbs
        breadcrumbs.push({ label: 'My Videos', to: '/my-videos' });
        breadcrumbs.push({ label: meetingTitle, to: '#' });
      }
      
      setBreadcrumbsOptions(breadcrumbs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingTitle, meetingData, setBreadcrumbsOptions]);

  // Confirm meeting view callback (similar to old viewer pattern)
  const recordSectionProgress = useCallback(
    (sectionId) => {
      if (
        !hasTrainingPathAssignment ||
        !sectionId ||
        completedSectionsRef.current.has(sectionId)
      ) {
        return;
      }

      completedSectionsRef.current.add(sectionId);

      // Optimistic update for immediate UI feedback
      updateTrainingPathProgress(id);

      TrainingPathService.recordSectionProgress({
        assignmentId: trainingPathAssignmentId,
        sectionId,
        meetingId: id,
        progress: 1,
      }).then(() => {
        // Invalidate section progress query to update check marks
        queryClient.invalidateQueries(['section-progress', trainingPathAssignmentId, id]);
      }).catch(() => {
        completedSectionsRef.current.delete(sectionId);
        // TODO: Revert optimistic update on failure
      });
    },
    [hasTrainingPathAssignment, trainingPathAssignmentId, id, updateTrainingPathProgress]
  );

  // Track current item for section completion logic
  // (Removed automatic completion on navigation - now only completes on natural playback end)
  useEffect(() => {
      lastTimelineItemRef.current = currentItem || null;
  }, [currentItem]);

  useEffect(() => {
    if (
      !hasTrainingPathAssignment ||
      isPlaying ||
      !currentItem ||
      !currentItem.metadata?.sectionId
    ) {
      return;
    }

    const itemEnd = currentItem.start_time + (currentItem.duration || 0);
    if (!itemEnd) {
      return;
    }

    if (currentTime >= itemEnd - 0.1) {
      recordSectionProgress(currentItem.metadata.sectionId);
    }
  }, [
    currentItem,
    currentTime,
    hasTrainingPathAssignment,
    isPlaying,
    recordSectionProgress,
  ]);

  useEffect(() => {
    if (!hasTrainingPathAssignment) {
      return () => {};
    }

    return () => {
      const lastSectionId =
        lastTimelineItemRef.current?.metadata?.sectionId ||
        currentItem?.metadata?.sectionId;
      if (lastSectionId) {
        recordSectionProgress(lastSectionId);
      }
    };
  }, [currentItem, hasTrainingPathAssignment, recordSectionProgress]);

  // Reset training path completion modal when navigating to a different video
  useEffect(() => {
    setTrainingPathCompleteModalData(null);
    setTrainingPathCompletionDismissed(false);
    dispatch(resetPlayback()); // Reset playback state to prevent stale currentTime from triggering completion modal
  }, [id, dispatch]);

  // Detect when training path video reaches the end and show completion modal
  useEffect(() => {
    // Ensure timeline is loaded for the CURRENT video (not stale from previous)
    if (timelineItems.length === 0) {
      return;
    }

    if (
      !hasTrainingPathAssignment ||
      !totalDuration ||
      currentTime < totalDuration - 0.1 || // Allow small tolerance
      trainingPathCompleteModalData || // Don't show if already visible
      trainingPathCompletionDismissed // Don't re-show if user dismissed
    ) {
      return;
    }

    // Timeline end detected for training path

    // Check if there are more videos in the training path
    if (trainingPathData?.content && trainingPathData.content.length > 0) {
      const currentVideoIndex = trainingPathData.content.findIndex(video => video.id === id);
      const hasNextVideo = currentVideoIndex >= 0 && currentVideoIndex < trainingPathData.content.length - 1;

      // Training path completion check

      // Showing training path completion modal
      // Mark the final section as complete when timeline ends
      if (currentItem?.metadata?.sectionId) {
        recordSectionProgress(currentItem.metadata.sectionId);
      }
      setTrainingPathCompleteModalData({ isLastVideo: !hasNextVideo });
      setTrainingPathCompletionDismissed(false);
    }
  }, [
    hasTrainingPathAssignment,
    currentTime,
    totalDuration,
    trainingPathData,
    id,
    trainingPathCompleteModalData,
    trainingPathCompletionDismissed,
    timelineItems.length,
  ]);

  const confirmMeetingView = useCallback(() => {
    // Only confirm view in viewer mode (not preview/editor mode)
    if (preview) {
      return;
    }

    // Prevent duplicate calls
    if (isViewConfirmedRef.current) {
      return;
    }

    // Confirm view for anonymous users (with email)
    if (email) {
      MeetingService.confirmMeetingViewAnonymously({ meetingId: id, email })
        .then(() => {
          isViewConfirmedRef.current = true;
        })
        .catch((anonymousViewError) => {
          console.error('Failed to confirm anonymous meeting view:', anonymousViewError);
        });
    } 
    // Confirm view for authenticated users
    else if (isAuthenticated) {
      MeetingService.confirmMeetingView(id)
        .then(() => {
          isViewConfirmedRef.current = true;
        })
        .catch((meetingViewError) => {
          console.error('Failed to confirm meeting view:', meetingViewError);
        });
    }
  }, [email, id, isAuthenticated, preview]);

  // Track previous meeting ID/version to detect when we're loading a different meeting
  const prevMeetingKeyRef = useRef(null);

  // Load meeting into Redux when data is available
  useEffect(() => {
    if (!meetingData || !meetingData.Sections || meetingData.Sections.length === 0) {
      // Clear data if no sections
      dispatch(clearAssets());
      dispatch(clearTimeline());
      return;
    }

    // Create a key to identify this meeting (id + version)
    const shouldLoadDraft =
      ['creator', 'contributor', 'reviewer'].includes(normalizedUserRole) ||
      Boolean(reviewId);

    const currentMeetingKey = `${id}-${
      shouldLoadDraft ? 'draft' : meetingVersion || 'latest'
    }`;
    const isNewMeeting = prevMeetingKeyRef.current !== currentMeetingKey;

    // Reset view confirmation when loading a new meeting
    if (isNewMeeting) {
      isViewConfirmedRef.current = false;
    }

    // Only reload Redux state when loading a NEW meeting (different ID or version)
    // This prevents clearing everything when refetching (e.g., after answering a question)
    if (!isNewMeeting) {
      return;
    }

    // If meeting prop is provided (shared meeting flow), convert sections directly
    // without making another API call (which would fail for anonymous users)
    if (meeting && meeting.Sections) {
      try {
        const { assets, timelineItems: items } = convertSectionsToTimeline(meeting.Sections);

        // Clear existing data
        dispatch(clearAssets());
        dispatch(clearTimeline());

        // Load new data
        dispatch(addAssets(assets));
        dispatch(reorderItems(items));

        // Set preview mode
        dispatch(setPreviewMode(true));

        // Clear modal lifecycle state when loading new meeting
        dispatch(clearAllHandledSections());

        // Reset timeline pagination to first page when meeting loads
        setCurrentSectionsPage(0);

        // Initialize meeting at the beginning
        if (items.length > 0) {
          dispatch(selectItem(items[0].id));
          dispatch(seek(0));
        }

        // Update ref to track this meeting
        prevMeetingKeyRef.current = currentMeetingKey;
        return;
      } catch (sectionConversionError) {
        console.error('Failed to convert shared meeting sections:', sectionConversionError);
        return;
      }
    }

    // For regular meetings (not shared), use the normal loader
    // Backend handles filtering for both PUBLISHED and DRAFT meetings,
    // returning the correct sections based on meeting status and draftData
    loadPublishedMeeting({ meetingId: id, meetingVersion }).then(async ({ assets, timelineItems: items }) => {

      // Clear existing data
      dispatch(clearAssets());
      dispatch(clearTimeline());

      // Load new data
      dispatch(addAssets(assets));
      dispatch(reorderItems(items));

      // Handle resume functionality for training paths
      if (hasTrainingPathAssignment && items.length > 0) {
        let targetSectionId = resumeSectionId;

        // If no explicit resumeSection provided, resume to last played section
        if (!targetSectionId) {
          try {
            const progressData = await TrainingPathService.getSectionProgress(
              trainingPathAssignmentId,
              id // current meeting ID
            );

            if (progressData && progressData.length > 0) {
              // Use the most recent progress record (last played section)
              const lastPlayedSection = progressData[0]; // Already sorted by updatedAt DESC
              targetSectionId = lastPlayedSection.sectionId;
            }
          } catch (progressFetchError) {
            // Silently fail - continue without resume
          }
        }

        if (targetSectionId) {
          // Find the section in the timeline and seek to its start time
          const resumeSection = items.find(item => item.metadata?.sectionId === targetSectionId);

          if (resumeSection) {
            // Delay to ensure timeline and video player are fully ready
            setTimeout(() => {
              dispatch(seek(resumeSection.start_time));
            }, 1000);
          }
        }
      }

      // Set preview mode
      dispatch(setPreviewMode(true));

      // Clear modal lifecycle state when loading new meeting
      dispatch(clearAllHandledSections());

      // Reset timeline pagination to first page when meeting loads
      setCurrentSectionsPage(0);

      // Initialize meeting at the beginning
      if (items.length > 0) {
        dispatch(selectItem(items[0].id));
        dispatch(seek(0));
      }

      // Update ref to track this meeting
      prevMeetingKeyRef.current = currentMeetingKey;
    }).catch((meetingLoadError) => {
      console.error('Failed to load meeting:', meetingLoadError);
    });
  }, [
    meetingData,
    meeting,
    id,
    meetingVersion,
    dispatch,
    setCurrentSectionsPage,
    isMobile,
    preview,
    normalizedUserRole,
    reviewId,
  ]);

  // Confirm meeting view when meeting data is loaded (viewer mode only)
  useEffect(() => {
    // Only confirm view in viewer mode (not preview/editor mode)
    if (preview) {
      return;
    }

    // Wait for meeting data to be available
    if (!meetingData || !meetingData.id) {
      return;
    }

    // Confirm view (callback handles duplicate prevention)
    confirmMeetingView();
  }, [meetingData, preview, confirmMeetingView]);

  // Cleanup: Reset all state when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Clear all Redux state when leaving the viewer
      dispatch(clearAssets());
      dispatch(clearTimeline());
      dispatch(resetPlayback());
      dispatch(clearAllHandledSections());
      dispatch(setQuestionModalVisible(false));
      dispatch(setViewImageSectionModalVisible(false));
      dispatch(setViewUploadDocumentsModalVisible(false));
      dispatch(setPreviewMode(false));
      
      // Reset the meeting key ref and view confirmation
      prevMeetingKeyRef.current = null;
      isViewConfirmedRef.current = false;
    };
  }, [dispatch]);

  // Spacebar to play/pause
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle spacebar
      if (e.key !== ' ' && e.code !== 'Space') return;

      // Don't interfere if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        e.target.isContentEditable
      ) {
        return;
      }

      // Prevent default behavior (scrolling)
      e.preventDefault();
      dispatch(togglePlayPause());
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch]);

  const refetchMeeting = useCallback(async () => {
    if (meeting) {
      getSharedMeeting({ password: accessPassword });
      return;
    }
    const result = await refetch();
    
    // After refetch, update Redux assets with new answer data
    if (result?.data?.Sections) {
      const updatedSections = result.data.Sections;
      
      // Update question assets with new answers
      updatedSections.forEach((section) => {
        if (section.type === 'question' && section.Question?.Answers) {
          const asset = allAssets.find(
            (a) => a.metadata?.sectionId === section.id && a.type === 'question'
          );
          
          if (asset) {
            dispatch(updateAsset({
              id: asset.id,
              changes: {
                metadata: {
                  ...asset.metadata,
                  questionAnswers: section.Question.Answers,
                },
              },
            }));
          }
        }
      });
    }
  }, [accessPassword, getSharedMeeting, meeting, refetch, allAssets, dispatch]);

  // Only redirect to sign-in if:
  // 1. User is not authenticated
  // 2. Query actually ran (enabled) and failed (isError)
  // 3. We don't have meeting data from props (shared meeting flow)
  // This allows anonymous users to view shared meetings via SharedMeetingPage
  if (!isAuthenticated && isError && !meeting) {
    const query = reviewId ? `reviewId=${reviewId}` : '';
    setRedirectAfterLogin(`/my-videos/${id}?${query}`);
    navigate('/sign-in');
  }

  useEffect(() => {
    if (!data?.MeetingReviews.length) {
      return;
    }

    const revId = data.MeetingReviews[0].id;
    queryParams.set('reviewId', revId);
    const updatedSearchString = queryParams.toString();
    const updatedUrl = `${window.location.pathname}?${updatedSearchString}`;
    navigate(updatedUrl);
  }, [data?.MeetingReviews]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsSignInHintVisible(true);
    } else {
      setIsSignInHintVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (data) {
      setQuestions(
        data.Sections?.filter((section) => section.type === 'question')?.map(
          (section) => section.Question
        )
      );
    }
  }, [data]);

  useEffect(() => {
    if (meeting) {
      setQuestions(
        meeting.Sections?.filter((section) => section.type === 'question')?.map(
          (section) => section.Question
        )
      );
    }
  }, [meeting]);

  useEffect(() => {
    if (user && meeting) {
      getSharedMeeting({ password: accessPassword, userId: user.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Get showViewerNotesModal from UiContext
  const { showViewerNotesModal } = useContext(UiContext);

  // Helper function to show modal for non-video sections
  const showModalForNonVideoSection = useCallback((item, asset) => {
    if (!item || !asset) return;

    // Non-playable types: question, image, document, text (note)
    const nonPlayableTypes = ['question', 'image', 'document', 'text'];
    const isNonPlayable = nonPlayableTypes.includes(asset.type);

    if (!isNonPlayable) return;

    // Find current section
    const currentSection = item.metadata?.sectionId
      ? sections.find((s) => s.id === item.metadata.sectionId)
      : null;

    if (!currentSection) return;

    const sectionId = currentSection.id;

    // For question sections: check if user has already answered
    if (currentSection.type === 'question' && currentSection.Question) {
      const currentUserId = user?.id;
      if (currentUserId) {
        const userAnswer = currentSection.Question.Answers?.find(
          (answr) => answr.User?.id === currentUserId
        );
        if (userAnswer) {
          // User has already answered - mark as handled and don't show modal
          dispatch(markSectionAsHandled(sectionId));
          return;
        }
      }
    }

    // Don't show if this section has already been handled (shown and closed/answered)
    if (handledSectionIds.includes(sectionId)) {
      return;
    }

    // Pause playback
    dispatch(pause());

    // Show appropriate modal based on type
    if (currentSection.type === 'question') {
      dispatch(setQuestionModalVisible(true));
    } else if (currentSection.type === 'image') {
      dispatch(setViewImageSectionModalVisible(true));
    } else if (currentSection.type === 'document') {
      dispatch(setViewUploadDocumentsModalVisible(true));
    } else if (currentSection.type === 'note' || currentSection.type === 'text') {
      // Use UiContext for text/note sections
      const nextItem = (() => {
        const currentIndex = timelineItems.findIndex((timelineItem) => timelineItem.id === item.id);
        if (currentIndex >= 0 && currentIndex < timelineItems.length - 1) {
          return timelineItems[currentIndex + 1];
        }
        return null;
      })();

      showViewerNotesModal(currentSection.text || '', () => {
        // Mark section as handled when notes modal closes
        dispatch(markSectionAsHandled(sectionId));
        if (nextItem) {
          dispatch(selectItem(nextItem.id));
          dispatch(seek(nextItem.start_time));
          dispatch(play());
        } else {
          dispatch(seek(totalDuration));
        }
      });
    }
  }, [sections, timelineItems, dispatch, totalDuration, showViewerNotesModal, handledSectionIds, user]);

  // Detect non-video sections during playback and show modals
  useEffect(() => {
    if (!currentItem || !currentAsset || !isPlaying) {
      return;
    }

    // Don't show modal if any modal is already visible (prevents re-showing during close/transition)
    if (isAnyModalVisible) {
      return;
    }

    showModalForNonVideoSection(currentItem, currentAsset);
  }, [currentItem, currentAsset, isPlaying, isAnyModalVisible, showModalForNonVideoSection]);

  // Clear handled section when we leave its time range
  // This allows re-showing the modal if user seeks back to the section
  useEffect(() => {
    if (!currentItem || !currentItem.metadata?.sectionId || handledSectionIds.length === 0) {
      return;
    }

    const currentSectionId = currentItem.metadata.sectionId;
    const currentTimeValue = currentTime || 0;

    // Check all handled sections - if we're outside their time range, clear them
    handledSectionIds.forEach((sectionId) => {
      if (sectionId === currentSectionId) {
        // We're in this section's time range, keep it handled
        return;
      }

      // Find the item for this handled section
      const handledItem = timelineItems.find(
        (item) => item.metadata?.sectionId === sectionId
      );

      if (handledItem) {
        const handledItemStartTime = handledItem.start_time || 0;
        const handledItemEndTime = handledItemStartTime + (handledItem.duration || 0);

        // If we're outside this section's time range, clear it from handled
        if (currentTimeValue < handledItemStartTime || currentTimeValue > handledItemEndTime) {
          dispatch(clearHandledSection(sectionId));
        }
      }
    });
  }, [currentItem, currentTime, timelineItems, handledSectionIds, dispatch]);

  // Render preview using new infrastructure
  const renderPreview = () => {
    // Show Snapsop logo when any modal is visible
    if (isAnyModalVisible) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#0b0b0b',
            backgroundImage: `url(${snapsopLogo})`,
            backgroundRepeat: 'repeat',
            backgroundSize: '140px auto',
            backgroundPosition: '0 0',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(11, 11, 11, 0.90)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          <img
            src={snapsopLogo}
            alt="SnapSOP"
            style={{
              width: 200,
              height: 200,
              opacity: 0.3,
              zIndex: 2,
              position: 'relative',
            }}
          />
        </div>
      );
    }

    if (!currentAsset || !currentItem) {
      return (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#e5e7eb' }}>
            Preview Area
          </div>
          <div style={{ fontSize: 13, marginTop: 6, color: '#9ca3af' }}>
            No content available
          </div>
        </div>
      );
    }

    if (currentAsset.type === 'video') {
      return <VideoRenderer asset={currentAsset} currentItem={currentItem} />;
    }

    if (currentAsset.type === 'audio') {
      return <AudioRenderer asset={currentAsset} currentItem={currentItem} />;
    }

    const props = {
      asset: currentAsset,
      currentItem,
    };
    if (currentAsset.type === 'image') return <ImageRenderer {...props} />;
    if (currentAsset.type === 'document')
      return <DocumentRenderer {...props} />;
    if (currentAsset.type === 'question')
      return <QuestionRenderer {...props} />;
    if (currentAsset.type === 'text')
      return <TextRenderer {...props} />;
    if (currentAsset.type === 'assigned-section')
      return <AssignedSectionRenderer {...props} />;
    return <div>Unknown asset type</div>;
  };

  const openConfirmReviewModal = (decision) => {
    setIsConfirmReviewModalVisible(true);
    setReviewDecision(decision);
  };

  const commentsSource = meeting || { Comments: comments };

  if (isSignUpPopupVisible) {
    return <SignIn handleClose={() => setIsSignUpPopupVisible(false)} />;
  }

  const trainingPathContent = trainingPathData?.content || [];
  const trainingPathName = assignmentData?.TrainingPath?.name || '';
  const isLoadingPlaylistData = isLoadingAssignment || isLoadingTrainingPath;
  const hasCompletedCurrentVideo =
    Boolean(trainingPathCompleteModalData) || trainingPathCompletionDismissed;
  const playlistContent = trainingPathContent.map((video) => {
    const isCurrent = String(video.id) === String(id);
    if (isCurrent && hasCompletedCurrentVideo) {
      return { ...video, progressPercent: 100 };
    }
    return video;
  });

  const trainingPathPlaylistData = hasTrainingPathAssignment ? {
    trainingPathName,
    content: playlistContent,
    currentMeetingId: id,
    trainingPathAssignmentId,
    isLoading: isLoadingPlaylistData,
  } : null;

  return (
    <Layout
      hasUserBlock={isAuthenticated}
      style={{ overflowX: 'hidden' }}
      trainingPathPlaylist={trainingPathPlaylistData}
    >
      <div className={classes.Meeting}>
        <div
          className={classNames(classes.breadcrumbsContainer, {
            [newClasses.reviewerBanner]:
              reviewId && normalizedUserRole === 'reviewer',
          })}
        >
          <Breadcrumbs
            breadcrumbsOptions={breadcrumbsOptions}
            editableLastItem={
              canEditMeeting &&
              normalizedUserRole !== 'reviewer' &&
              !meetingVersion &&
              meetingData?.id
                ? {
                    canEdit: true,
                    onUpdate: (newTitle) =>
                      updateMeeting({ meetingId: id, title: newTitle }),
                  }
                : undefined
            }
          />
          <div className={newClasses.topActions}>
          {canEditMeeting && normalizedUserRole !== 'reviewer' && !meetingVersion && (
            <FilledButton
              width={120}
              style={{ height: 50 }}
              onClick={() => {
                navigate(`/my-videos/${id}/edit`);
              }}
            >
              Edit
            </FilledButton>
          )}
          {meetingVersion && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor: '#e8e8e8',
                color: '#333',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Version {meetingVersion}
            </span>
          )}
          {meetingVersion && (
            <FilledButton
              width={100}
              style={{ height: 50, marginLeft: 12 }}
              onClick={() => {
                navigate(`/my-videos/${id}/history`);
              }}
            >
              Back
            </FilledButton>
          )}
            {reviewId && normalizedUserRole === 'reviewer' && (
              <VideoApproval
                openConfirmReviewModal={openConfirmReviewModal}
                variant="inline"
              />
            )}
          </div>
        </div>
        <div className={newClasses.viewerLayout}>
          <div className={newClasses.sideRail}>
            <div className={classes.headerContainer}>
              {!isMobile && <Navigation />}
            </div>
          </div>
          <div className={newClasses.contentWrapper}>
            {/* New player infrastructure */}
            <div className={newClasses.previewSection}>
            <PreviewContainer
              videoRef={videoRef}
              isAudioEnabled={false}
              isRecording={false}
              isTimerRunning={false}
              isCameraEnabled={false}
              isScreenEnabled={false}
              streamRef={miniVideoRef}
              timerValue={0}
              recordingTime={0}
              isCameraRecordingPaused={false}
              renderPreview={() => (
                <>
                  {/* Header with back arrow and meeting title - mobile only */}
                  {isMobile && (
                    <div className={`${headerStyles.Header} ${newClasses.previewHeader}`}>
                      <div className={headerStyles.buttonContainer}>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (document.fullscreenElement) {
                              document.exitFullscreen();
                              return;
                            }
                            navigate(-1);
                          }}
                        >
                          <img src={backIcon} alt="Back" />
                        </button>
                        <h2 className={headerStyles.title}>{meetingTitle}</h2>
                      </div>
                    </div>
                  )}
                  <div className={newClasses.previewContent}>{renderPreview()}</div>
                  {totalDuration > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 10,
                      }}
                    >
                      <SeekingBar
                        currentItem={currentItem}
                        currentAsset={currentAsset}
                        fullscreenContainerRef={fullscreenContainerRef}
                      />
                    </div>
                  )}
                </>
              )}
              startCountdownThenStart={() => {}}
              handlePauseRecording={() => {}}
              handleResumeRecording={() => {}}
              handleStopRecording={() => {}}
              cancelArming={() => {}}
              currentItem={currentItem}
              currentAsset={currentAsset}
              isViewerMode
              containerStyle={previewContainerStyle}
            />
          </div>

            {/* Footer Timeline - shown on both desktop and mobile */}
            {!meetingVersion && (
              <div className={newClasses.timelineSection}>
              <FooterTimeline
                ref={footerTimelineRef}
                sectionsListContainerRef={sectionsListContainerRef}
                sectionsListRef={sectionsListRef}
                sectionsCount={sectionsCount}
                currentSectionsPage={currentSectionsPage}
                sectionsPageCount={sectionsPageCount}
                sectionsListTranslateX={sectionsListTranslateX}
                scrollSections={scrollSections}
                handleDragOver={() => {}}
                handleSectionDrop={() => {}}
                handleDragEnd={() => {}}
                timelineItems={timelineItems}
                allAssets={allAssets}
                isPreviewMode
                plusSignRef={null}
                isMenuVisible={false}
                setMenuVisible={() => {}}
                selectItem={(itemId) => {
                  dispatch(selectItem(itemId));
                  const item = timelineItems.find((i) => i.id === itemId);
                  if (item) {
                    dispatch(seek(item.start_time));
                    // Check if this is a non-video item and show modal
                    const asset = allAssets.find((a) => a.id === item.asset_id);
                    if (asset) {
                      showModalForNonVideoSection(item, asset);
                    }
                  }
                }}
                currentItem={currentItem}
                draggedItemId={null}
                dragOverIndex={null}
                focusedSectionTitleId={null}
                hoveredSectionTitleId={null}
                isEditMode={false}
                handleSectionTitleChange={() => {}}
                handleOpenEditQuestion={() => {}}
                handleOpenEditText={() => {}}
                handleDragStart={() => {}}
                isItemAllowedToDrag={() => false}
                removeTimelineItem={() => {}}
                user={user}
                fetchedSections={sections}
                completedSectionIds={completedSectionIds}
                isTrainingView={hasTrainingPathAssignment}
              className={isMobile ? newClasses.mobileTimeline : undefined}
              />
              </div>
            )}
          </div>
        </div>

        {!preview && !meetingVersion && (
          <Comments
            shouldNotify={meetingData?.shouldNotify}
            commentsSource={commentsSource}
            questions={questions}
            user={user}
            refetchMeeting={refetchMeeting}
            videoRef={videoRef}
            id={id}
            setIsSignUpPopupVisible={setIsSignUpPopupVisible}
            rewindVideoByComment={(meetingTime) => {
              dispatch(seek(meetingTime));
              dispatch(play());
            }}
            isAuthenticated={isAuthenticated}
            refetchComments={refetchComments}
            transcript={meetingData?.transcript}
            setIsPlaying={(playing) => {
              if (playing) {
                dispatch(play());
              } else {
                dispatch(pause());
              }
            }}
            meeting={meeting}
            signedS3Url={meetingData?.signedS3Url}
            videoDuration={totalDuration}
            isPlaying={isPlaying}
            audioSectionsWithoutBackground={[]}
            miniVideoRef={miniVideoRef}
            currentTime={currentTime}
          />
        )}
        <ConfirmReviewModal
          show={isConfirmReviewModalVisible}
          handleClose={() => setIsConfirmReviewModalVisible(false)}
          meetingId={id}
          reviewId={reviewId}
          reviewDecision={reviewDecision}
          email={user?.email || email}
        />

        {/* Viewer Modals Container */}
        <ViewerModalsContainer
          currentItem={currentItem}
          sections={sections}
          refetchMeeting={refetchMeeting}
          onModalClose={returnToFullscreen}
        />

        {/* Training Path Completion Modal */}
        {trainingPathCompleteModalData && (
          <MultiButtonDialogModal
            show
            handleClose={() => {
              setTrainingPathCompleteModalData(null);
              setTrainingPathCompletionDismissed(true);
            }}
            title={
              trainingPathCompleteModalData?.isLastVideo
                ? "🎉 Training path completed"
                : "Video Complete!"
            }
            description={
              trainingPathCompleteModalData?.isLastVideo
                ? "Congratulations! You have successfully completed all videos in this training path. Organization admin will verify your training completion soon."
                : "Would you like to continue with the next video in your training path?"
            }
            buttons={
              trainingPathCompleteModalData?.isLastVideo
                ? [
                    {
                      title: 'Exit Training',
                      handleClick: () => {
                        // Navigate back to training path detail page
                        navigate(`/training-paths/${assignmentData?.TrainingPath?.id}?acl=trainee`);
                      },
                      color: 'primary',
                      testId: 'exit-training',
                    },
                  ]
                : [
                    {
                      title: 'Play Next Video',
                      handleClick: () => {
                        // Find next video and navigate to it
                        const currentVideoIndex = trainingPathContent.findIndex(video => video.id === id);
                        if (currentVideoIndex >= 0 && currentVideoIndex < trainingPathContent.length - 1) {
                          const nextVideo = trainingPathContent[currentVideoIndex + 1];
                          navigate(`/my-videos/${nextVideo.id}?trainingPathAssignmentId=${trainingPathAssignmentId}`);
                        }
                      },
                      color: 'primary',
                      testId: 'play-next-video',
                    },
                    {
                      title: 'Exit Training',
                      handleClick: () => {
                        // Navigate back to training path detail page
                        navigate(`/training-paths/${assignmentData?.TrainingPath?.id}?acl=trainee`);
                      },
                      color: 'secondary',
                      testId: 'exit-training',
                    },
                  ]
            }
          />
        )}
      </div>
    </Layout>
  );
}

export default function MeetingPageNew({
  meeting,
  accessPassword,
  getSharedMeeting,
  preview,
  email,
}) {
  return (
    <CreateVideoNewPageProvider>
      <MeetingPageNewContent
        meeting={meeting}
        accessPassword={accessPassword}
        getSharedMeeting={getSharedMeeting}
        preview={preview}
        email={email}
      />
    </CreateVideoNewPageProvider>
  );
}
